import React from "react";

import { Box, Typography, useTheme } from "@mui/material";
import { MuiTelInput, type MuiTelInputInfo } from "mui-tel-input";

import { FieldLabel } from "../FieldLabel";
import {
  FIELD_FONT_SIZE,
  FIELD_RADIUS,
  fieldAccentColor,
  fieldBorderColor,
  fieldErrorColor,
  fieldErrorRing,
  fieldFill,
  fieldFocusRing,
  fieldHoverRing,
} from "../../../theme/fieldChrome";
import { MENU_PAPER_SX } from "../../../theme/menuChrome";

export interface PhoneFieldProps {
  value: string;
  onChange: (value: string, info?: MuiTelInputInfo) => void;
  label?: string;
  /** ISO country code used to seed the dial code, e.g. "US". */
  defaultCountry?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  name?: string;
}

/**
 * VRG-490 — shared, formatted phone input. Extracted to `@repo/ui` per
 * working-conventions §36/§47 (the second app needing a country-aware,
 * auto-formatted phone field — `apps/platform`'s 2FA SMS enrollment had none
 * at all and fell back to a bare `Input`, so a user typing `12032536986` got
 * exactly that back with no formatting and no country picker).
 *
 * `apps/app` already had a phone field (`components/Fields/PhoneField.tsx`)
 * — NOT migrated onto this one. That component wraps the same `mui-tel-input`
 * core but pairs it with a mobile bottom-sheet country picker (`SelectDrawer`)
 * and the app's own field-height tokens, which is the correct affordance for
 * a phone reached by tapping an NFC tag (§37) and was not reported as
 * defective. What's shared here is the hard-won part — `mui-tel-input`
 * wrapping + house field chrome — using `mui-tel-input`'s OWN built-in
 * dropdown for country selection, which is the desktop-appropriate picker
 * for `apps/platform`'s admin console. Per §47's three-layer model this is a
 * legitimate layer-2 (app-specific) variation on a shared layer-1 core, not
 * drift — a future consolidation could rebuild `apps/app`'s field on top of
 * this one, but that is a larger, separate change and out of this task's
 * scope.
 *
 * Absolute px throughout (no `theme.spacing()`) per §29/§47 — this file
 * renders under both apps' themes, which disagree on the spacing base.
 */
export const PhoneField = ({
  value,
  onChange,
  label,
  defaultCountry = "US",
  error,
  helperText,
  disabled = false,
  fullWidth = true,
  placeholder,
  autoFocus,
  name,
}: PhoneFieldProps) => {
  const theme = useTheme();

  return (
    <Box>
      <FieldLabel label={label} />
      <MuiTelInput
        variant="filled"
        name={name}
        value={value}
        onChange={(v, info) => onChange(v, info)}
        defaultCountry={defaultCountry as never}
        disabled={disabled}
        fullWidth={fullWidth}
        placeholder={placeholder}
        error={error}
        autoFocus={autoFocus}
        // Same house treatment as the shared `Input` (rest primary[100] ->
        // hover/focus brand[600] + a soft ring), read from the same
        // `fieldChrome` source rather than re-derived here (§31).
        InputProps={{ disableUnderline: true }}
        // VRG-490 round 2 — the country picker is a real MUI `Menu`
        // (`mui-tel-input`'s internal `<Menu>` around the flag list), so it
        // already inherits the theme's `MuiMenu.paper` chrome (border,
        // radius, no dark-mode overlay). What was broken was POSITION and
        // SIZE, not chrome: MUI's default Menu `variant` is `"selectedMenu"`,
        // which aligns the SELECTED country with the anchor point rather than
        // anchoring the paper below the field — over a ~240-row list that
        // produced exactly what was reported: an unanchored panel spanning
        // nearly the full viewport height. `variant="menu"` turns that
        // alignment off; explicit anchor/transform origins then place the
        // paper directly below-left of the field like every other dropdown
        // in the app (Select, ActionDropdown, Combobox).
        MenuProps={{
          variant: "menu",
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          slotProps: {
            paper: {
              sx: {
                ...MENU_PAPER_SX,
                // Bounded + scrollable, matching the shared `Select`'s
                // dropdown (`ITEM_HEIGHT * ITEMS_TO_SHOW`) rather than
                // `MENU_PAPER_SX`'s own near-viewport-height ceiling, which
                // is the right default for a short kebab/action menu but
                // would still read as "too tall" over 240 rows.
                maxHeight: 320,
                minWidth: 280,
              },
            },
          },
        }}
        sx={{
          "& .MuiFilledInput-root": {
            /*
             * ON THE ROOT, and that is the whole reason this field was TALLER
             * than every other one beside it.
             *
             * MUI declares the input's `line-height: 1.4375em`, and `em`
             * resolves against the element's own inherited font-size — which
             * comes from the ROOT. Setting 16px only on `.MuiFilledInput-input`
             * (as this did) left the root at body1's 20px, so the line box
             * computed 20 x 1.4375 = 28.75px instead of 23px. Measured in the
             * browser: this field 55px against the shared `Input`'s 49-50px, on
             * identical 12px padding.
             *
             * The shared `Input` already documents this exact trap at its own
             * `fontSize: FIELD_FONT_SIZE`; PhoneField simply had not followed
             * it. Same token, so the two cannot drift.
             */
            fontSize: FIELD_FONT_SIZE,
            borderRadius: `${FIELD_RADIUS}px`,
            border: `1px solid ${fieldBorderColor(theme)}`,
            backgroundColor: fieldFill(theme),
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            paddingTop: "12px",
            paddingBottom: "12px",
            "&:hover": {
              backgroundColor: fieldFill(theme),
              borderColor: disabled ? fieldBorderColor(theme) : fieldAccentColor(theme),
              boxShadow: disabled ? "none" : fieldHoverRing(theme),
            },
            "&.Mui-focused, &:focus-within": {
              backgroundColor: fieldFill(theme),
              borderColor: fieldAccentColor(theme),
              boxShadow: fieldFocusRing(theme),
            },
            "&.Mui-error": {
              borderColor: fieldErrorColor(theme),
              "&:hover": { borderColor: fieldErrorColor(theme) },
              "&.Mui-focused, &:focus-within": {
                borderColor: fieldErrorColor(theme),
                boxShadow: fieldErrorRing(theme),
              },
            },
            opacity: disabled ? 0.4 : 1,
          },
          "& .MuiFilledInput-input": {
            fontSize: 16,
            paddingTop: 0,
            paddingBottom: 0,
          },
          /*
           * THE FLAG SITS LOW WITHOUT THIS, and the cause is a label that is not
           * there. MUI's FilledInput reserves room at the top for a floating
           * label by pushing the start adornment down (`margin-top: 16px`, plus
           * hiding it until focus). This field renders its label ABOVE the
           * control via `FieldLabel` (§31), so nothing ever occupies that space
           * — the margin just drops the flag a few px below the "+1" beside it,
           * which is exactly how it read.
           *
           * `alignSelf: center` rather than a compensating negative margin: the
           * row's height is set by its padding, so centring stays correct if
           * that padding ever changes.
           */
          "& .MuiInputAdornment-root": {
            marginTop: "0 !important",
            alignSelf: "center",
            height: "auto",
            maxHeight: "none",
          },
          /*
           * THE FLAG BUTTON WAS SETTING THE ROW'S HEIGHT.
           *
           * `mui-tel-input` puts the flag in a real `IconButton`, which carries
           * MUI's default 8px padding on all four sides. Inside a root that
           * already adds 12px top and bottom, the button's own box — not the
           * text — became the tallest thing in the row, so this field stood
           * several px taller than every other one on the page. The shared
           * `Input` (Settings -> Profile's Phone, and every text field beside
           * it) is 12px padding around a single line box and nothing else.
           *
           * Zeroing the padding and letting the flag size itself off the line
           * box puts the two at the same height, which is the parity that
           * matters: these fields sit in the same forms.
           */
          "& .MuiTelInput-IconButton": {
            padding: 0,
            marginRight: "8px",
            "&:hover": { backgroundColor: "transparent" },
          },
          "& .MuiTelInput-Flag": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />
      {helperText && (
        // NOT `variant="body3"` — that custom variant is declared in a MUI
        // Typography module augmentation apps/platform's tsconfig picks up
        // and apps/app's does not, so `packages/ui` compiles under the
        // LATTER's stricter view (§47's `FieldLabel` hits the identical gap
        // and takes the same way out: explicit sx, not the variant prop).
        <Typography
          component="span"
          sx={{ display: "block", mt: "12px", fontSize: 14, lineHeight: "18px" }}
          color={error ? "error.main" : "text.secondary"}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};
